import React, { useState, useEffect, useCallback } from 'react';
import { organizationService } from '../services';
import { Search, Building2, Calendar, CreditCard, Clock, XCircle, Loader2, Filter } from 'lucide-react';
import { Pagination, Modal, Select } from '../components';
import { planService } from '../services';

/**
 * Organization Subscription Plans Page
 * Moderately Compact Design (Balanced readability and data density)
 */
const OrganizationPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(null);

  // Filter States
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    subscription_status: '',
    organization_type: '',
    plan_id: ''
  });
  const [activeFilters, setActiveFilters] = useState({
    subscription_status: '',
    organization_type: '',
    plan_id: ''
  });
  const [plansList, setPlansList] = useState([]);

  const LIMIT = 15; // Set to a reasonable number for standard screens

  const fetchOrganizationPlans = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await organizationService.getAllOrganizationPlans({
        page,
        limit: LIMIT,
        search: submittedSearch,
        subscription_status: activeFilters.subscription_status,
        plan_id: activeFilters.plan_id,
        organization_type: activeFilters.organization_type
      });

      if (response.success) {
        setPlans(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
      } else {
        setError('Failed to load organization plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(err.message || 'Error loading plans');
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, activeFilters]);

  /**
   * Fetch Available Plans for Filter
   */
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await planService.getAllPlans();
        if (response.success) {
          setPlansList(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchOrganizationPlans(currentPage);
  }, [fetchOrganizationPlans, currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmittedSearch(searchTerm);
    setCurrentPage(1);
  };

  /**
   * Change a subscription's status (active/trial) inline from the table.
   */
  const handleSubscriptionStatusChange = async (item, newStatus) => {
    if (newStatus === item.subscription_status) return;
    try {
      setStatusUpdating(item.id);
      const response = await organizationService.updateSubscriptionStatus(item.id, newStatus);
      if (response.success) {
        setPlans((prev) =>
          prev.map((p) =>
            p.id === item.id
              ? {
                  ...p,
                  subscription_status: response.data?.subscription_status ?? newStatus,
                  billing_cycle: response.data?.billing_cycle ?? p.billing_cycle,
                  trial_start_date: response.data?.trial_start_date ?? p.trial_start_date,
                  trial_end_date: response.data?.trial_end_date ?? p.trial_end_date,
                  updatedAt: response.data?.updatedAt ?? p.updatedAt,
                }
              : p
          )
        );
      }
    } catch (err) {
      console.error('Error updating subscription status:', err);
      setError(err.message || 'Failed to update subscription status');
    } finally {
      setStatusUpdating(null);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSubmittedSearch('');
    setFilters({ subscription_status: '', organization_type: '', plan_id: '' });
    setActiveFilters({ subscription_status: '', organization_type: '', plan_id: '' });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setActiveFilters(filters);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const hasActiveFilters = activeFilters.subscription_status !== '' || activeFilters.organization_type !== '' || activeFilters.plan_id !== '';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-4 pb-8 w-full animate-in fade-in duration-500 font-sans">

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-2">
        <div>
          <h1 className="text-[22px] font-semibold text-[#1E293B] dark:text-gray-200 tracking-tight">Subscription Plans</h1>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-0.5">
            Manage and monitor organization subscription statuses and billing cycles
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white dark:bg-[#262b33] rounded-[12px] shadow-sm border border-[#E2E8F0] dark:border-gray-800 overflow-hidden">

        {/* Search & Filter Bar */}
        <div className="p-3.5 border-b border-[#E2E8F0] dark:border-gray-800 flex flex-col sm:flex-row justify-end items-center gap-3 bg-white dark:bg-[#262b33]">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search by organization name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-[#CBD5E1] dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[13px] text-[#0F172A] dark:text-gray-200 transition-colors bg-[#F8FAFC] dark:bg-[#31373f]"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] dark:text-gray-500 hover:text-[#64748B] dark:hover:text-gray-400"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex w-full sm:w-auto gap-2">
              <button
                type="submit"
                className="bg-[#2563EB] hover:bg-blue-700 text-white px-3.5 py-2 rounded-lg transition-all shadow-sm flex items-center justify-center shrink-0"
                title="Search"
              >
                <Search className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => setShowFilterModal(true)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border transition-all text-[13px] font-medium shrink-0 ${hasActiveFilters
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/50 text-blue-600'
                  : 'bg-white dark:bg-[#31373f] border-[#CBD5E1] dark:border-gray-700 text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                <Filter className={`w-4 h-4 ${hasActiveFilters ? 'fill-blue-600/10' : ''}`} />
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="flex items-center justify-center w-4 h-4 text-[10px] bg-blue-600 text-white rounded-full">
                    {[activeFilters.subscription_status, activeFilters.organization_type, activeFilters.plan_id].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Table Area */}
        <div className="relative min-h-[350px]">
          {loading ? (
            <div className="absolute inset-0 bg-white/50 dark:bg-[#262b33]/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
                <p className="text-[13px] font-medium text-[#64748B] dark:text-gray-400">Loading plans...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-[13px]">
                {error}
              </div>
              <button
                onClick={() => fetchOrganizationPlans(currentPage)}
                className="block mx-auto mt-3 text-[13px] text-blue-600 font-medium hover:underline"
              >
                Try Again
              </button>
            </div>
          ) : plans.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-[#F8FAFC] dark:bg-[#20242c] border-b border-[#E2E8F0] dark:border-gray-800">
                    <th className="px-4 py-3 text-[11px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Organization</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Plan</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Billing / Trial Details</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]/60 dark:divide-gray-800">
                  {plans.map((item) => (
                    <tr key={item.id} className="hover:bg-[#F8FAFC]/80 dark:hover:bg-[#31373f] transition-colors group">

                      {/* Organization Column */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                            <Building2 className="w-3.5 h-3.5 text-[#2563EB]" />
                          </div>
                          <span className="text-[13px] font-medium text-[#1E293B] dark:text-gray-200">
                            {item.Organization?.name || 'Unknown'}
                          </span>
                        </div>
                      </td>

                      {/* Plan Column */}
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col">
                          <span className="text-[13px] font-medium text-[#1E293B] dark:text-gray-200">{item.plan?.name || '-'}</span>
                          <span className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">£{item.plan?.price || 0} / month</span>
                        </div>
                      </td>

                      {/* Status Column — editable subscription tier (active/trial)
                          when the org is live. If the org is suspended/archived/
                          deleted it shows that lifecycle stage (read-only, since
                          that is controlled from the organization itself). */}
                      <td className="px-4 py-2.5">
                        {item.Organization && item.Organization.is_active === false ? (
                          <span
                            className="inline-block px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider bg-red-100 dark:bg-red-900/10 text-red-700 dark:text-red-400"
                            title="Controlled by the organization's lifecycle status"
                          >
                            {item.Organization.status || 'Inactive'}
                          </span>
                        ) : (
                          (() => {
                            const busy = statusUpdating === item.id;
                            return (
                              <div className="relative inline-flex items-center gap-1.5">
                                <Select
                                  value={item.subscription_status || 'trial'}
                                  disabled={busy}
                                  onChange={(v) => handleSubscriptionStatusChange(item, v)}
                                  size="sm"
                                  options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'trial', label: 'Trial' },
                                  ]}
                                  className="w-[120px]"
                                />
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />}
                              </div>
                            );
                          })()
                        )}
                      </td>

                      {/* Details Column */}
                      <td className="px-4 py-2.5">
                        {item.subscription_status === 'active' ? (
                          <div className="flex items-center gap-1.5 text-[12px] text-[#475569] dark:text-gray-400">
                            <CreditCard className="w-3.5 h-3.5 text-[#94A3B8] dark:text-gray-500" />
                            <span className="capitalize">{item.billing_cycle || 'Monthly'} billing</span>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5 text-[12px] text-[#475569] dark:text-gray-400">
                              <Clock className="w-3.5 h-3.5 text-[#94A3B8] dark:text-gray-500" />
                              <span>Start: {formatDate(item.trial_start_date)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[12px] text-[#475569] dark:text-gray-400">
                              <Calendar className="w-3.5 h-3.5 text-[#94A3B8] dark:text-gray-500" />
                              <span>End: {formatDate(item.trial_end_date)}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Updated Date Column */}
                      <td className="px-4 py-2.5">
                        <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                          {formatDate(item.updatedAt || item.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 flex flex-col items-center justify-center text-center px-4">
              <div className="w-12 h-12 bg-[#F1F5F9] dark:bg-[#31373f] rounded-full flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-[#94A3B8] dark:text-gray-500" />
              </div>
              <h3 className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 mb-1">No subscription plans found</h3>
              <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-4 max-w-sm">
                {submittedSearch ? "No matching results." : "Plans will appear here once organizations are created."}
              </p>
              {submittedSearch && (
                <button onClick={clearFilters} className="text-[#2563EB] text-[12px] font-medium hover:underline">
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination Section */}
        {plans.length > 0 && (
          <div className="border-t border-[#E2E8F0] dark:border-gray-800 p-3 bg-[#F8FAFC] dark:bg-[#20242c]">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      {/* Filter Modal */}
      <Modal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title="Filter Subscription Plans"
        maxWidth="max-w-md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <button
              onClick={() => {
                setFilters({ subscription_status: '', organization_type: '', plan_id: '' });
                setActiveFilters({ subscription_status: '', organization_type: '', plan_id: '' });
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-[13px] font-medium text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200 transition-colors"
            >
              Reset All
            </button>
            <button
              onClick={applyFilters}
              className="px-6 py-2 bg-[#2563EB] text-white rounded-lg hover:bg-blue-700 transition shadow-sm text-[13px] font-medium"
            >
              Apply Filters
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Status Filter */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Subscription Status</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setFilters({ ...filters, subscription_status: filters.subscription_status === 'active' ? '' : 'active' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.subscription_status === 'active'
                  ? 'bg-green-50 dark:bg-emerald-900/10 border-green-200 dark:border-emerald-900/50 text-green-700 dark:text-emerald-400'
                  : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilters({ ...filters, subscription_status: filters.subscription_status === 'trial' ? '' : 'trial' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.subscription_status === 'trial'
                  ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-400'
                  : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                Trial
              </button>
              <button
                onClick={() => setFilters({ ...filters, subscription_status: filters.subscription_status === 'inactive' ? '' : 'inactive' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.subscription_status === 'inactive'
                  ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400'
                  : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                Inactive
              </button>
            </div>
          </div>

          {/* Organization Type Filter */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Organization Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFilters({ ...filters, organization_type: 'carehome' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.organization_type === 'carehome'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/50 text-blue-700 dark:text-blue-400'
                  : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                Care Home
              </button>
              <button
                onClick={() => setFilters({ ...filters, organization_type: 'domiciliary' })}
                className={`py-2 px-3 rounded-lg border text-[13px] font-medium transition-all ${filters.organization_type === 'domiciliary'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/50 text-blue-700 dark:text-blue-400'
                  : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                Domiciliary
              </button>
            </div>
          </div>

          {/* Plan Filter */}
          <div className="space-y-2">
            <label className="text-[12px] font-bold text-[#64748B] dark:text-gray-400 uppercase tracking-wider">Plan Tier</label>
            <Select
              value={filters.plan_id}
              onChange={(v) => setFilters({ ...filters, plan_id: v })}
              options={[
                { value: '', label: 'All Plans' },
                ...plansList.map((plan) => ({ value: String(plan.id), label: plan.name })),
              ]}
              className="w-full"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationPlans;